import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { smasheursService } from '../services/api';
import api from '../services/api';
import CarteDeVisite from '../components/CarteDeVisite';

const SmasheurDetail = () => {
  const { id } = useParams();
  const [smasheur, setSmasheur] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'numSaucisse', direction: 'desc' });

  const loadSmasheur = useCallback(async () => {
    try {
      const [smasheurResponse, achievementsResponse] = await Promise.all([
        smasheursService.getWithTournois(id),
        api.get(`/smasheurs/${id}/achievements`).then(r => r.data)
      ]);
      setSmasheur(smasheurResponse.data);
      setAchievements(achievementsResponse);
    } catch (error) {
      console.error('Error loading smasheur:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSmasheur();
  }, [loadSmasheur]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!smasheur) {
    return (
      <div className="container">
        <div className="alert alert-danger">Smasheur non trouvé</div>
        <Link to="/smasheurs" className="btn btn-primary">
          Retour à la liste
        </Link>
      </div>
    );
  }

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const parseNumSaucisse = (num) => {
    const s = String(num);
    if (s.includes('+')) {
      const [base, add] = s.split('+');
      return parseFloat(base) + parseFloat(add) * 0.1;
    }
    return parseFloat(s.replace(',', '.'));
  };

  const getSortedClassements = () => {
    if (!smasheur.classements) return [];

    const sorted = [...smasheur.classements].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'numSaucisse') {
        aValue = parseNumSaucisse(aValue);
        bValue = parseNumSaucisse(bValue);
      }

      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  };

  return (
    <div className="container">
      <Link to="/smasheurs" className="btn btn-outline-secondary mb-4">
        <i className="bi bi-arrow-left me-2"></i>
        Retour à la liste
      </Link>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center mb-4">
            <div className="col-auto">
              {smasheur.profileImage ? (
                <img
                  src={smasheur.profileImage}
                  alt={smasheur.pseudo}
                  className="rounded-circle"
                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                />
              ) : (
                <i className="bi bi-person-circle" style={{ fontSize: '100px' }}></i>
              )}
            </div>
            <div className="col">
              <div className="d-flex align-items-center">
                <h1 className="card-title mb-0">{smasheur.pseudo}</h1>
                <CarteDeVisite
                  smasheur={smasheur}
                  achievements={achievements}
                  classements={smasheur.classements}
                />
              </div>
            </div>
          </div>

          {smasheur.description && (
            <p className="fst-italic mb-3" style={{ color: '#fff' }}>{smasheur.description}</p>
          )}

          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>Tag:</strong>{' '}
                <span className="badge bg-secondary fs-6">{smasheur.tag}</span>
              </p>
              <p>
                <strong>Personnages:</strong> {smasheur.personnages || 'Aucun personnage enregistré'}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Points totaux:</strong>{' '}
                <span className="badge bg-warning text-dark fs-5">{smasheur.points || 0}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques de positions */}
      {smasheur.classements && smasheur.classements.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <h3 className="card-title mb-4">
              <i className="bi bi-trophy me-2"></i>
              Statistiques de positions
            </h3>
            <div className="row">
              {(() => {
                // Compter les positions
                const positionCounts = {};
                smasheur.classements.forEach(c => {
                  positionCounts[c.position] = (positionCounts[c.position] || 0) + 1;
                });

                // Trier par position
                const sortedPositions = Object.entries(positionCounts)
                  .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

                return sortedPositions.map(([position, count]) => (
                  <div key={position} className="col-md-6 col-lg-4 mb-3">
                    <div className="card text-center">
                      <div className="card-body">
                        <h5 className="card-title">
                          {position === '1' && <i className="bi bi-trophy-fill text-warning me-2"></i>}
                          {position === '2' && <i className="bi bi-trophy-fill text-secondary me-2"></i>}
                          {position === '3' && <i className="bi bi-trophy-fill text-danger me-2"></i>}
                          {position}
                          {position === '1' ? 'ère' : 'ème'} place
                        </h5>
                        <p className="card-text display-6 mb-0">{count}</p>
                        <small className="text-muted">fois</small>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Succès / Achievements */}
      {achievements.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <h3 className="mb-0">
              <i className="bi bi-trophy-fill me-2"></i>
              Succès
            </h3>
          </div>
          <div className="card-body">
            {/* Succès de Classement */}
            <h4 className="mb-3">
              <i className="bi bi-award me-2"></i>
              Succès de Classement
            </h4>
            <div className="row mb-4">
              {achievements
                .filter(achievement => ['victory', 'top3', 'top8', 'top17', 'top33'].includes(achievement.type))
                .map((achievement) => (
                  <div key={achievement.id} className="col-md-6 col-lg-4 mb-4">
                    <div className={`card h-100 ${achievement.earned ? 'border-warning' : 'border-secondary opacity-50'}`}>
                      <div className="card-body">
                        <div className="d-flex align-items-start mb-3">
                          <div className="flex-shrink-0 me-3">
                            {achievement.earned ? (
                              <i className="bi bi-award-fill text-warning" style={{ fontSize: '2rem' }}></i>
                            ) : (
                              <i className="bi bi-award text-muted" style={{ fontSize: '2rem' }}></i>
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="card-title mb-1">{achievement.title}</h5>
                            <p className="card-text small mb-2" style={{ color: '#fff' }}>
                              {achievement.description}
                            </p>
                            {!achievement.earned && (
                              <div className="progress mb-2" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-warning"
                                  role="progressbar"
                                  style={{ width: `${achievement.progress}%` }}
                                  aria-valuenow={achievement.progress}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                            )}
                            <p className="small mb-0">
                              {achievement.earned ? (
                                <span className="badge bg-success">Débloqué ✓</span>
                              ) : (
                                <span className="badge bg-secondary">
                                  {achievement.count} / {achievement.threshold}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        {achievement.earned && achievement.relatedTournaments.length > 0 && (
                          <div className="mt-3">
                            <p className="small text-muted mb-2">
                              <strong>Tournois concernés:</strong>
                            </p>
                            <div className="d-flex flex-wrap gap-1">
                              {[...achievement.relatedTournaments]
                                .sort((a, b) => parseFloat(a.numSaucisse) - parseFloat(b.numSaucisse))
                                .map((tournament, idx) => (
                                  <Link
                                    key={idx}
                                    to={`/tournois/${tournament.id}`}
                                    className="badge bg-primary text-decoration-none"
                                  >
                                    #{tournament.numSaucisse}
                                    {tournament.position && ` (${tournament.position}${tournament.position === 1 ? 'er' : 'e'})`}
                                  </Link>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Succès de Participation */}
            <h4 className="mb-3">
              <i className="bi bi-calendar-check me-2"></i>
              Succès de Participation
            </h4>
            <div className="row">
              {achievements
                .filter(achievement => ['participation', 'streak'].includes(achievement.type))
                .map((achievement) => (
                  <div key={achievement.id} className="col-md-6 col-lg-4 mb-4">
                    <div className={`card h-100 ${achievement.earned ? 'border-warning' : 'border-secondary opacity-50'}`}>
                      <div className="card-body">
                        <div className="d-flex align-items-start mb-3">
                          <div className="flex-shrink-0 me-3">
                            {achievement.earned ? (
                              <i className={`bi ${achievement.type === 'streak' ? 'bi-fire' : 'bi-calendar-check-fill'} text-warning`} style={{ fontSize: '2rem' }}></i>
                            ) : (
                              <i className={`bi ${achievement.type === 'streak' ? 'bi-fire' : 'bi-calendar-check'} text-muted`} style={{ fontSize: '2rem' }}></i>
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="card-title mb-1">{achievement.title}</h5>
                            <p className="card-text small mb-2" style={{ color: '#fff' }}>
                              {achievement.description}
                            </p>
                            {!achievement.earned && (
                              <div className="progress mb-2" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-warning"
                                  role="progressbar"
                                  style={{ width: `${achievement.progress}%` }}
                                  aria-valuenow={achievement.progress}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                            )}
                            <p className="small mb-0">
                              {achievement.earned ? (
                                <span className="badge bg-success">Débloqué ✓</span>
                              ) : (
                                <span className="badge bg-secondary">
                                  {achievement.count} / {achievement.threshold}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        {achievement.earned && achievement.relatedTournaments.length > 0 && (
                          <div className="mt-3">
                            <p className="small text-muted mb-2">
                              <strong>Tournois concernés:</strong>
                            </p>
                            <div className="d-flex flex-wrap gap-1">
                              {[...achievement.relatedTournaments]
                                .sort((a, b) => parseFloat(a.numSaucisse) - parseFloat(b.numSaucisse))
                                .map((tournament, idx) => (
                                  <Link
                                    key={idx}
                                    to={`/tournois/${tournament.id}`}
                                    className="badge bg-primary text-decoration-none"
                                  >
                                    #{tournament.numSaucisse}
                                    {tournament.position && ` (${tournament.position}${tournament.position === 1 ? 'er' : 'e'})`}
                                  </Link>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Liste détaillée des participations */}
      <div className="card">
        <div className="card-body">
          <h3 className="card-title mb-4">
            <i className="bi bi-list-ol me-2"></i>
            Participations aux tournois ({smasheur.classements?.length || 0})
          </h3>

          {smasheur.classements && smasheur.classements.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort('numSaucisse')}
                      style={{ cursor: 'pointer' }}
                    >
                      Saucismash
                      {sortConfig.key === 'numSaucisse' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th
                      onClick={() => handleSort('position')}
                      style={{ cursor: 'pointer' }}
                    >
                      Position
                      {sortConfig.key === 'position' && (
                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedClassements().map((classement) => (
                    <tr key={classement.id}>
                      <td>
                        <Link
                          to={`/tournois/${classement.tournoiId}`}
                          className="text-decoration-none"
                        >
                          <span className="badge bg-primary">
                            Saucismash #{classement.numSaucisse}
                          </span>
                        </Link>
                      </td>
                      <td>
                        <span className={`badge ${
                          classement.position === 1 ? 'bg-warning text-dark' :
                          classement.position === 2 ? 'bg-secondary' :
                          classement.position === 3 ? 'bg-danger' :
                          'bg-dark'
                        }`}>
                          {classement.position === 1 && <i className="bi bi-trophy-fill me-1"></i>}
                          {classement.position === 2 && <i className="bi bi-trophy-fill me-1"></i>}
                          {classement.position === 3 && <i className="bi bi-trophy-fill me-1"></i>}
                          {classement.position}
                          {classement.position === 1 ? 'ère' : 'ème'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              Aucune participation trouvée pour ce smasheur
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmasheurDetail;